import { isEqual } from 'lodash-es';
import { useContext } from 'react';

import { useStoreSelector } from '../../../core/store/selector.js';
import { XYChartStoreContext } from '../context/XYChartStore.context.js';
import type { XYChartState } from '../types/state.js';

export const useSelector = <T>(
  selector: (state: XYChartState) => T,
  compareFn?: (a: T, b: T) => boolean,
): T => useStoreSelector(XYChartStoreContext, selector, compareFn);

export const useSetState = () => {
  const { setState } = useContext(XYChartStoreContext);

  return setState;
};

export const useContentState = () =>
  useSelector(
    (state: XYChartState) => state.explore.content,
    (a, b) => {
      const prevPoint = a.datapoint;

      const newPoint = b.datapoint;
      return (
        a.type === b.type &&
        prevPoint?.x0 === newPoint?.x0 &&
        prevPoint?.x1 === newPoint?.x1 &&
        prevPoint?.y0 === newPoint?.y0 &&
        prevPoint?.y1 === newPoint?.y1
      );
    },
  );

export const useVisibleState = () =>
  useSelector((state: XYChartState) => state.explore.visible);

export const usePinnedState = () =>
  useSelector((state: XYChartState) => state.explore.pinned);

export const usePositionState = () =>
  useSelector((state: XYChartState) => state.explore.position, isEqual);

export const useToolbarState = () =>
  useSelector((state) => state.toolbar, isEqual);

export const useXYChartHighlightedSeriesIdsState = () =>
  useSelector((state: XYChartState) => state.highlightedSeriesIds, isEqual);

export const useInteractionModeState = () =>
  useSelector((state) => state.interactionMode);

export const useZoomState = () => useSelector((state) => state.zoom, isEqual);

export const useCurrentDomainState = () =>
  useSelector((state) => state.currentDomain, isEqual);

export const useSelectState = () => useSelector((state) => state.select);

export const useHoveredThresholdState = () =>
  useSelector((state) => state.thresholds.hoveredThresholdId);

export const useAnnotationsState = () =>
  useSelector((state) => state.annotations);
